import { KeyboardEvent, MutableRefObject, ReactNode, Ref, useState } from "react";
import { SparkleIcon } from "@phosphor-icons/react";
import { TrashIcon } from "lucide-react";
import { AppButton, AppTextarea, ConfirmationModal, FigmaTypography, isEnterSubmit } from "@libs/zapi-ui/custom";
import { KeyReturnIcon } from "@libs/zapi-ui/assets";
import { cn } from "@/zapi-frontend/shared/lib/utils";

type TChatShellProps = {
  children: ReactNode;
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  title?: string;
  placeholder?: string;
  isBusy?: boolean;
  isComposerDisabled?: boolean;
  // Lets a chat focus its message box, e.g. when a button asks the user to type their own answer.
  inputRef?: Ref<HTMLTextAreaElement>;
  headerRight?: ReactNode;
  controls?: ReactNode;
  controlsLeft?: ReactNode;
  topBar?: ReactNode;
  submitLabel?: string;
  canSubmit?: boolean;
  inputExtras?: ReactNode;
  hideComposer?: boolean;
  onClear?: () => void;
  canClear?: boolean;
  clearConfirm?: { title: string; description: string; confirmText?: string };
  scrollRef?: MutableRefObject<HTMLDivElement | null>;
  className?: string;
  bodyClassName?: string;
  "data-testid"?: string;
  inputTestId?: string;
  sendTestId?: string;
  clearTestId?: string;
};

// The layout is the Project List chat panel, which is the UI reference for INV-2361.
export function ChatShell({
  children,
  input,
  onInputChange,
  onSubmit,
  title = "Chat",
  placeholder = "Type a message to AI...",
  isBusy = false,
  isComposerDisabled = false,
  inputRef,
  headerRight,
  controls,
  controlsLeft,
  topBar,
  submitLabel = "Send",
  canSubmit,
  inputExtras,
  hideComposer = false,
  onClear,
  canClear = true,
  clearConfirm,
  scrollRef,
  className,
  bodyClassName,
  "data-testid": dataTestId,
  inputTestId,
  sendTestId,
  clearTestId
}: TChatShellProps) {
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isEnterSubmit(e)) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <aside data-testid={dataTestId} className={cn("flex shrink-0 flex-col justify-between bg-white", className)}>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-center justify-between px-2 h-14 border-b border-innoveraZinc-100 shrink-0">
          <div className="flex items-center gap-1 px-3 py-2 h-8 rounded-lg">
            <SparkleIcon className="size-4 text-innoveraZinc-500" weight="regular" />
            <FigmaTypography component="span" variant="label-2" color="zinc500">
              {title}
            </FigmaTypography>
          </div>
          {headerRight}
        </div>

        <div ref={scrollRef} className={cn("flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4", bodyClassName)}>
          {children}
        </div>
      </div>

      {!hideComposer && (
      <div className="p-[8px]">
        {topBar}
        <div className="flex flex-col gap-2 py-1 relative border border-innoveraZinc-150 rounded-[8px] bg-white">
          {inputExtras}
          <AppTextarea
            ref={inputRef}
            fullWidth
            className="h-[80px] resize-none !border-0 !ring-0"
            placeholder={placeholder}
            rows={5}
            value={input}
            // Dictation input sends NBSP for line breaks; normalize once for every chat.
            onChange={(e) => onInputChange(e.target.value.replace(/\u00a0/g, "\n"))}
            onKeyDown={handleKeyDown}
            disabled={isBusy || isComposerDisabled}
            data-testid={inputTestId}
          />
          <div className={cn("flex gap-2 items-center px-2 pb-1", controlsLeft ? "justify-between" : "justify-end")}>
            {controlsLeft && <div className="flex items-center gap-2">{controlsLeft}</div>}
            <div className="flex gap-2 items-center">
            {controls}
            <AppButton
              size="sm"
              type="button"
              endAdornment={<KeyReturnIcon />}
              disabled={isBusy || !(canSubmit ?? input.trim().length > 0)}
              onClick={onSubmit}
              data-testid={sendTestId}
            >
              {submitLabel}
            </AppButton>
            {onClear && (
              <AppButton
                appearance="outlined"
                color="zinc150"
                size="icon"
                type="button"
                disabled={!canClear}
                onClick={() => setShowClearConfirm(true)}
                aria-label="Clear chat"
                title="Clear chat"
                startAdornment={<TrashIcon className="size-4" />}
                data-testid={clearTestId}
              />
            )}
            </div>
          </div>
        </div>
      </div>
      )}

      {onClear && (
        <ConfirmationModal
          isOpen={showClearConfirm}
          onClose={() => setShowClearConfirm(false)}
          onConfirm={() => {
            onClear();
            setShowClearConfirm(false);
          }}
          title={clearConfirm?.title ?? "Clear Chat"}
          description={clearConfirm?.description ?? "This will remove all messages in this chat. Are you sure?"}
          confirmText={clearConfirm?.confirmText ?? "Clear"}
        />
      )}
    </aside>
  );
}
